// @mui material components
import Grid from "@mui/material/Grid";
import React from "react";
import {
  TextField,
  InputLabel,
  FormControl,
  OutlinedInput,
  IconButton,
  CircularProgress,
  Button,
  Box,
  Card,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { green } from "@mui/material/colors";
import Icon from "@mui/material/Icon";
import MDSnackbar from "components/MDSnackbar";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import { db, storage } from "../../../firebase";
import { doc, getDoc, setDoc, updateDoc, FieldValue } from "firebase/firestore";
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import { useParams } from "react-router-dom";
import CheckIcon from "@mui/icons-material/Check";

function Detail() {
  const { id } = useParams();
  const [data, setData] = React.useState({});
  const [snackbarOpen, setSnackbarOpen] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [file, setFile] = React.useState(null);
  const [imageProgress, setImageProgress] = React.useState(0);

  // Fetch document data
  React.useEffect(() => {
    const fetchData = async () => {
      const docRef = doc(db, "Experiences", id);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        setData(docSnap.data());
      } else {
        console.error("No document found with the given ID");
      }
    };
    fetchData();
  }, [id]);

  // Handle file upload
  React.useEffect(() => {
    if (!file) return;

    const uploadFile = () => {
      const storageRef = ref(storage, `Experiences/${file.name}`);
      const uploadTask = uploadBytesResumable(storageRef, file);

      uploadTask.on(
        "state_changed",
        (snapshot) => {
          const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          setImageProgress(progress);
        },
        (err) => console.error("File upload error:", err),
        () => {
          getDownloadURL(uploadTask.snapshot.ref).then((url) => {
            setData((prev) => ({ ...prev, image: url }));
          });
        }
      );
    };

    uploadFile();
  }, [file]);

  const updateData = async () => {
    try {
      setLoading(true);
  
      const docRef = doc(db, "Experiences", id);
      const docSnap = await getDoc(docRef);
  
      if (docSnap.exists()) {
        const existingData = docSnap.data();
  
        // Identify keys to remove
        const keysToRemove = Object.keys(existingData).filter(
          (key) => !(key in data)
        );
  
        // Prepare the update object
        const updateObject = { ...data };
  
        // Add deletions to the update object
        keysToRemove.forEach((key) => {
          updateObject[key] = FieldValue.delete();
        });
  
        // Update the document with new data and deletions
        await updateDoc(docRef, updateObject);
  
        setSnackbarOpen(true);
      } else {
        console.error("No document found with the given ID.");
      }
  
      setLoading(false);
    } catch (err) {
      console.error("Update error:", err);
      setLoading(false);
    }
  };

  const handleFieldChange = (field, value) => {
    setData((prev) => ({ ...prev, [field]: value }));
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Box py={3}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <Box pt={3} px={2}>
                <h6 style={{ textAlign: "center" }}>Experience Details</h6>
              </Box>
              <Box p={2}>
                {Object.keys(data).map((key) => (
                  <Box mb={2} key={key}>
                    {key === "image" ? (
                      <FormControl fullWidth>
                        <InputLabel htmlFor="upload-image">Experience Image</InputLabel>
                        <OutlinedInput
                          id="upload-image"
                          type="file"
                          onChange={(e) => setFile(e.target.files[0])}
                          endAdornment={
                            <IconButton>
                              {imageProgress === 100 ? <CheckIcon /> : null}
                            </IconButton>
                          }
                        />
                        {data[key] && (
                          <Box mt={2}>
                            <img src={data[key]} alt="Experience" style={{ maxWidth: "100%" }} />
                          </Box>
                        )}
                      </FormControl>
                    ) : typeof data[key] === "object" ? (
                      <>
                        {Object.entries(data[key]).map(([subKey, subValue], index) => (
                          <Box
                            key={`${key}-${index}`}
                            display="flex"
                            alignItems="center"
                            mb={1}
                          >
                            <TextField
                              label={`${key} Key`}
                              value={subKey}
                              onChange={(e) => {
                                const updatedObject = { ...data[key] };
                                const newKey = e.target.value;
                                updatedObject[newKey] = updatedObject[subKey];
                                delete updatedObject[subKey];
                                handleFieldChange(key, updatedObject);
                              }}
                              sx={{ mr: 2, flex: 1 }}
                            />
                            <TextField
                              label={`${key} Value`}
                              value={subValue}
                              onChange={(e) => {
                                const updatedObject = { ...data[key] };
                                updatedObject[subKey] = e.target.value;
                                handleFieldChange(key, updatedObject);
                              }}
                              sx={{ flex: 2 }}
                            />
                            <IconButton
                              onClick={() => {
                                const updatedObject = { ...data[key] };
                                delete updatedObject[subKey];
                                handleFieldChange(key, updatedObject);
                              }}
                            >
                              <CloseIcon />
                            </IconButton>
                          </Box>
                        ))}
                        <Button
                          variant="text"
                          color="info"
                          startIcon={<Icon>add</Icon>}
                          onClick={() => {
                            handleFieldChange(key, { ...data[key], "": "" });
                          }}
                        >
                          Add {key}
                        </Button>
                      </>
                    ) : (
                      <TextField
                        fullWidth
                        label={key}
                        value={data[key]}
                        onChange={(e) => handleFieldChange(key, e.target.value)}
                      />
                    )}
                  </Box>
                ))}
                <Button
                  variant="contained"
                  color="info"
                  onClick={updateData}
                  disabled={loading}
                >
                  {loading ? <CircularProgress size={24} /> : "Update Experience"}
                </Button>
              </Box>
            </Card>
          </Grid>
        </Grid>
      </Box>
      <Footer />
      <MDSnackbar
        open={snackbarOpen}
        onClose={() => setSnackbarOpen(false)}
        title="Update Successful"
        color="success"
        icon="check"
      />
    </DashboardLayout>
  );
}

export default Detail;
